import { Box, Button, Card, Dialog, DialogContent, Tooltip, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useState } from "react";
import { PieChart } from "react-minimal-pie-chart";

const PieCardContainer = styled((props) => (
    <Card elevation={5} {...props} />
))(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-evenly",
    padding: theme.spacing(2.5),
    alignItems: "center",
    width: "32rem",
    borderRadius: "3rem",
    overflow: "visible",
    height: "100%",
    [theme.breakpoints.up("sm")]: {
        width: "100%",
        flexDirection: "row",
        justifyContent: "space-around",
    },
    [theme.breakpoints.up("md")]: {
        flexDirection: "column",
        maxWidth: "32rem",
    },
    [theme.breakpoints.up("xl")]: {
        maxWidth: "100%",
        flexDirection: "row",
    },
}));

const PieColumn = styled(Box)(
    ({ theme }) => ({
        flexShrink: 0,
        width: "100%",
        [theme.breakpoints.up("sm")]: {
            width: "45%",
        },
        [theme.breakpoints.up("md")]: {
            width: "100%",
        },
        [theme.breakpoints.up("xl")]: {
            width: "45%",
        },
    })
);

const PieCardTitle = styled((props) => (
    <Typography variant="h3" {...props} />
))(({ theme }) => ({
    fontFamily: "montserrat",
    fontWeight: 800,
    fontSize: "2rem",
    margin: theme.spacing(2.5, 0, 1, 0),
    textAlign: "center",
}));

const PieLegendBox = styled(Box)(
    ({ theme }) => ({
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "space-around",
        marginBottom: theme.spacing(2),
    })
);

const PieLegend = styled("div", { shouldForwardProp: (prop) => prop !== "color" && prop !== "value" })(
    ({ theme, color, value }) => ({
        backgroundColor: color,
        height: 8,
        borderRadius: 10,
        width: "45%",
        maxWidth: "11rem",
        position: "relative",
        margin: theme.spacing(1.5, 0),
        [theme.breakpoints.up("xl")]: {
            margin: theme.spacing(1.75, 0),
        },
        "&::after": {
            position: "absolute",
            content: `'${value}'`,
            top: "100%",
            left: 3,
            right: 0,
            fontFamily: "'montserrat'",
            fontWeight: 600,
            fontSize: "1.1rem",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
        },
    })
);

const defaultColor = ["#003f5c", "#444e86", "#955196", "#dd5182", "#ff6e54", "#ffa600"];

export default function PieCard({ title, data, icon, color, ...rest }) {
    const [open, setOpen] = useState(false);
    data = data && Object.entries(data).sort((a, b) => b[1] - a[1]);
    const clipped = data && data.length > 6;
    const clippedData = data && data.slice(0, 6);
    const chartColor = color || defaultColor;

    return (
        <PieCardContainer {...rest}>
            <PieColumn>
                {(clippedData && clippedData.length) ? (
                    <PieChart
                        data={clippedData.map(([title, value], idx) => ({
                            title, value, color: chartColor[idx],
                        }))}
                        lineWidth={30}
                        labelPosition={0}
                        label={({ dataIndex }) => (dataIndex === 0 && icon)}
                        labelStyle={{ fontSize: "3.7rem" }}
                        rounded
                        animate
                        animationDuration={640}
                    />
                ) : (
                    <Typography sx={{
                        fontFamily: "inter",
                        textAlign: "center",
                        fontWeight: 500,
                        color: "#777",
                    }}>
                        NO DATA. <span style={{ fontSize: "1rem", display: "block" }}>This site is not delivered by a (supported) CDN.</span>
                    </Typography>
                )}
            </PieColumn>
            <PieColumn>
                <PieCardTitle>{title}</PieCardTitle>
                <PieLegendBox>
                    {clippedData && clippedData.map(([title, value], idx) => (
                        <Tooltip title={`${title} - ${value}`} key={`legend-${title}`}>
                            <PieLegend
                                color={chartColor[idx]}
                                value={`${title} - ${value}`}
                            />
                        </Tooltip>
                    ))}
                </PieLegendBox>
                {clipped && (
                    <Button
                        fullWidth
                        color="inherit"
                        sx={{
                            borderRadius: "3rem",
                            fontFamily: "montserrat",
                            fontWeight: 700,
                        }}
                        onClick={() => setOpen(true)}
                    >
                        Full List
                    </Button>
                )}
                <Dialog open={open} onClose={() => setOpen(false)}>
                    <DialogContent>
                        {data && data.map(([title, value], idx) => (
                            <Typography
                                key={idx}
                                sx={{
                                    fontFamily: "inter",
                                    fontWeight: 500,
                                    marginX: 1,
                                    marginY: 2,
                                    borderBottom: "solid 1px #aaa",
                                }}
                            >
                                {title} - {value}
                            </Typography>
                        ))}
                    </DialogContent>
                </Dialog>
            </PieColumn>
        </PieCardContainer>
    )
};